import React, { useState, useEffect } from 'react';
import { Table, Button, Spinner, Badge, Form, Row, Col, Alert, Card } from 'react-bootstrap';
import firebase from '../../../firebase';
import PaymentModal from './modal';
import { FaRegBuilding, FaCreditCard, FaMoneyBillAlt } from 'react-icons/fa'; // Import des icônes
import { toast } from 'react-toastify';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';

const PaymentTracking = () => {
    const [showList, setShowList] = useState(false);
    const [payments, setPayments] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [lateBookings, setLateBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [currentPayment, setCurrentPayment] = useState(null);
    const [searchTerm, setSearchTerm] = useState(''); // État pour le champ de recherche
    const [selectedType, setSelectedType] = useState(''); // État pour le filtre de type 
    const db = firebase.firestore();

    const getBadgeColor = (type) => {
        switch (type) {
            case 'Acompte':
                return 'success';
            case 'Solde':
                return 'warning';
            case 'Caution':
                return 'primary';
            default:
                return 'secondary';
        }
    };

    const getPaymentIcon = (method) => {
        switch (method) {
            case 'Virement':
                return <FaRegBuilding />;
            case 'Carte Bancaire':
                return <FaCreditCard />;
            case 'Espèces':
                return <FaMoneyBillAlt />;
            default:
                return null;
        }
    };

    // Fonction pour filtrer les paiements en fonction du nom du client et du type
    const filteredPayments = payments.filter((payment) => {
        const booking = bookings.find((b) => b.id === payment.bookingId);
        const matchesSearchTerm = booking && booking.client?.label.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = selectedType ? payment.type === selectedType : true;
        return matchesSearchTerm && matchesType;
    });

    useEffect(() => {
        const checkLatePayments = () => {
            const today = new Date();
    
            const late = bookings.filter(booking => {
                if (!booking.checkin?.seconds || booking.statut?.value !== "confirmed") return false;
    
                const checkinDate = booking.checkin.toDate(); // Date d'arrivée
                if (checkinDate < today) return false; // Exclure si la check-in date est déjà passée
    
                const dueDate = new Date(checkinDate);
                dueDate.setMonth(dueDate.getMonth() - 1); // La date limite pour le paiement
    
                return today > dueDate && (!booking?.acomptePaid || !booking?.soldeFinish);
            });
    
            setLateBookings(late);
        };
    
        if (bookings.length > 0) {
            checkLatePayments();
        }
    }, [bookings]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const paymentsSnapshot = await db.collection("payments").orderBy("date", "desc").get();
                const paymentsData = paymentsSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                const bookingsSnapshot = await db.collection("bookings").orderBy("checkin", "desc").get();
                const bookingsData = bookingsSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setPayments(paymentsData);
                setBookings(bookingsData);
                setLoading(false);
            } catch (error) {
                console.error("Erreur lors du chargement des données :", error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSavePayment = async (payment) => {
        try {
            if (payment.id) {
                // Modification d'un paiement existant
                payment.date = new Date(payment.date)
                await db.collection("payments").doc(payment.id).update(payment);
                setPayments((prev) =>
                    prev.map((p) => (p.id === payment.id ? { ...p, ...payment } : p))
                );
            } else {
                // Ajout d'un nouveau paiement
                const newPaymentRef = await db.collection("payments").add({
                    ...payment,
                    date: new Date(payment.date)
                });
                const newPayment = { id: newPaymentRef.id, ...payment };
                setPayments((prev) => [newPayment, ...prev]);
            }
            setShowModal(false);
        } catch (error) {
            console.error("Erreur lors de l'ajout/modification du paiement :", error);
        }
    };

    return (
        <div className=" mt-2">
            <h2>Suivi des Paiements</h2>
            {!loading && (<Row className="g-3 mb-3">
                <Col md={3} xxl={2} >
                    <Card className="h-100">
                        <FalconCardHeader
                            title="Retard de paiement"
                            titleTag="h6"
                            className="py-2"
                            light
                        />
                        <Card.Body
                            className={'h-100'}
                            as={Flex}
                            direction="column"
                            alignItems="between"
                        >
                            <h2 className="fw-normal text-700 mb-1 lh-1">{lateBookings.length}</h2>
                            <Button variant="outline-danger" onClick={() => setShowList(!showList)}>
                                {showList ? "Masquer la liste" : "Afficher la liste"}
                            </Button>
                            <div className="text-center mt-3">
                                <p className="fs--1 mb-0">soldes et cautions</p>
                                <p className="fs--1 mb-0">Quand le paiement n’a pas été effectué un mois avant la date d’arrivée..</p>
                            </div>
                        </Card.Body>
                    </Card>

                </Col>
            </Row>)}

            {  showList &&  lateBookings.length > 0 && (
                <Alert variant="danger">
                    <h5>Réservations en retard de paiement</h5>
                    <ul>
                        {lateBookings.map(booking => (
                            <li key={booking.id}><a href={`/booking/edit/${booking.id}`} target='_blank'>{booking.client?.label}</a> - Arrivée: {booking.checkin?.toDate().toLocaleDateString()}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            {/* Filtre et champ de recherche sur la même ligne */}
            <Form.Group className="mb-3">
                <Row>
                    {/* Champ de recherche */}
                    <Col md={2}>
                        <Form.Control
                            type="text"
                            placeholder="Rechercher par nom de client"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Col>

                    {/* Filtre sur le type de paiement */}
                    <Col md={2}>
                        <Form.Select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                        >
                            <option value="">Filtrer par type de paiement</option>
                            <option value="Acompte">Acompte</option>
                            <option value="Solde">Solde</option>
                            <option value="Caution">Caution</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Button variant="primary" onClick={() => { setCurrentPayment(null); setShowModal(true); }}>
                            Ajouter un paiement
                        </Button>
                    </Col>
                </Row>
            </Form.Group> 

            {loading ? (
                <div className="text-center mt-3">
                    <Spinner animation="border" />
                </div>
            ) : (
                <Table striped bordered hover className="mt-3">
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Montant</th>
                            <th>Type</th>
                            <th>Méthode</th>
                            <th>Date</th>
                            <th>Total Attendu</th> {/* Nouvelle colonne */}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPayments.map((payment) => {
                            const booking = bookings.find((b) => b.id === payment.bookingId);

                            // Déterminer le total attendu en fonction du type de paiement
                            let totalAttendu = "-";
                            if (booking) {
                                if (payment.type === "Solde") {
                                    totalAttendu = `${(parseFloat(booking.rent) -
                                        parseFloat(booking.acompte || "0") +
                                        parseFloat(booking.cleaningStay || "0") +
                                        parseFloat(booking.exitCleaning || "0") +
                                        parseFloat(booking.baififf || "0")) +
                                        parseFloat(booking.security || "0")}`;
                                }
                                else if (payment.type === "Acompte") totalAttendu = `${booking.acompte}`;
                                else if (payment.type === "Caution") totalAttendu = `${booking.security}`;
                            }
                            return (
                                <tr key={payment.id}>
                                    <td style={{ alignContent: 'center' }}>
                                        {booking ? <a href={`/booking/edit/${payment.bookingId}`} target='_blank'>
                                            <span>{booking.client?.label}<br />
                                                {booking.checkin?.seconds && booking.checkin.toDate().toLocaleDateString()}  → {booking.checkout?.seconds && booking.checkout.toDate().toLocaleDateString()}<br />({booking?.statut?.label})
                                            </span>
                                        </a> : "Réservation inconnue"}
                                    </td>
                                    <td style={{ alignContent: 'center' }}>{payment.amount} €</td>
                                    <td style={{ alignContent: 'center' }}>
                                        <Badge bg={getBadgeColor(payment.type)}>
                                            {payment.type}
                                        </Badge><br />
                                        {/* si je clique ca copie */}
                                        <a href='#' style={{}} onClick={() => { navigator.clipboard.writeText(payment.reference); toast.success("Référence copiée dans le presse-papiers !"); }}>
                                            {payment.reference && <small>Réf. : {payment.reference}</small>}
                                        </a>
                                    </td>
                                    <td style={{ alignContent: 'center' }}>{getPaymentIcon(payment.method)}</td>
                                    <td style={{ alignContent: 'center' }}>{payment.date?.seconds ? payment.date?.toDate().toLocaleDateString() : new Date(payment.date)?.toLocaleDateString()}</td>
                                    <td style={{ alignContent: 'center' }}>
                                        {payments ? (
                                            <>
                                                {(() => {
                                                    // Filtrage des paiements correspondant à la réservation et au type
                                                    const filteredPayments = payments.filter(pay =>
                                                        pay.bookingId === booking.id &&
                                                        pay.type === payment.type
                                                    );

                                                    // Calcul du total payé
                                                    const totalPaye = filteredPayments.reduce((total, pay) => total + parseFloat(pay.amount || 0), 0).toFixed(2);;

                                                    // Vérification si le total payé est égal au total attendu
                                                    const isEqual = parseFloat(totalPaye) >= parseFloat(totalAttendu); 
                                                    if (isEqual && booking.id && ((payment.type === "Acompte" && !booking.acomptePaid) || (payment.type === "Solde" && !booking.soldeFinish))) {
                                                        let m = {}
                                                        if (payment.type === "Acompte") m = { acomptePaid: true };
                                                        if (payment.type === "Solde") m = { soldeFinish: true };
                                                        
                                                        db.collection("bookings").doc(booking.id).update(m).then(() => {
                                                            toast.success("Paiement comptabilisé !");
                                                            setBookings((prev) =>
                                                                prev.map((p) => (p.id === booking.id ? { ...p, ...m } : p))
                                                            );
                                                        })
                                                    }
                                                    return (
                                                        <>
                                                            {totalPaye} / {totalAttendu} €
                                                            {isEqual && <span style={{ color: 'green !important', fontSize: '16px', marginLeft: '10px' }}>✔️</span>}
                                                            <br />
                                                            <span style={{ fontSize: '12px', color: '#555' }}>
                                                                Nombre de paiements : {filteredPayments.length}
                                                            </span>
                                                        </>
                                                    );
                                                })()}
                                            </>
                                        ) : `0 / ${totalAttendu} €`}
                                    </td>
                                    <td style={{ alignContent: 'center' }}>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => { setCurrentPayment(payment); setShowModal(true); }}
                                        >
                                            Modifier
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            <PaymentModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                onSavePayment={handleSavePayment}
                bookings={bookings}
                currentPayment={currentPayment}
            />
        </div>
    );
};

export default PaymentTracking;